import { useState } from 'react'
import { ArrowLeft, CheckCircle2, Loader2, Mail, MessageSquare } from 'lucide-react'

/**
 * Public support / contact page rendered at /support (and the /help,
 * /contact aliases). App-store listings require a reachable Support URL, so
 * this must resolve to a real, crawlable page — the SPA fallback serves
 * index.html for every path and App.tsx routes to this component.
 *
 * Submissions are filed as `type='support_request'` tickets through the SAME
 * public feedback endpoint the app uses, so every request lands in the admin
 * Resolution Center (apps/admin/app/feedback) with the full status / reply /
 * resolve workflow — no separate support inbox to maintain.
 */
const API_BASE =
  ((import.meta as unknown as { env?: Record<string, string> }).env?.VITE_API_BASE) ||
  'https://api.bimauncle.com'

const SUPPORT_EMAIL = 'hello@bimauncle.com'

// Mirrors the feedback_tickets `category` values an agent would expect to triage.
const TOPICS = [
  { value: 'account', label: 'Account & login' },
  { value: 'calculation', label: 'Premium calculation' },
  { value: 'app_issue', label: 'App not working' },
  { value: 'billing', label: 'Billing & payouts' },
  { value: 'feature', label: 'Feature request' },
  { value: 'other', label: 'Something else' },
]

type FormState = {
  name: string
  email: string
  phone: string
  topic: string
  subject: string
  message: string
  website: string // honeypot
}

type Errors = Partial<Record<keyof FormState, string>>

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

function validate(s: FormState): Errors {
  const e: Errors = {}
  const name = s.name.trim()
  if (name.length < 2 || name.length > 80) e.name = 'Enter your name (2–80 characters).'
  if (!EMAIL_RE.test(s.email.trim())) e.email = 'Enter a valid email address.'
  const subject = s.subject.trim()
  if (subject.length < 3 || subject.length > 200) e.subject = 'Add a short subject (3–200 characters).'
  const message = s.message.trim()
  if (message.length < 10 || message.length > 5000)
    e.message = 'Tell us a bit more (10–5000 characters).'
  return e
}

export function SupportPage() {
  const [form, setForm] = useState<FormState>({
    name: '',
    email: '',
    phone: '',
    topic: 'account',
    subject: '',
    message: '',
    website: '',
  })
  const [errors, setErrors] = useState<Errors>({})
  const [submitting, setSubmitting] = useState(false)
  const [done, setDone] = useState<{ ref: string | null } | null>(null)
  const [topError, setTopError] = useState<string | null>(null)

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setTopError(null)

    const errs = validate(form)
    setErrors(errs)
    if (Object.keys(errs).length > 0) return

    setSubmitting(true)
    try {
      const res = await fetch(`${API_BASE}/v2/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'support_request',
          source: 'web_support',
          platform: 'web',
          severity: 'medium',
          category: form.topic,
          title: form.subject.trim(),
          description: form.message.trim(),
          route: '/support',
          reporter_name: form.name.trim(),
          reporter_email: form.email.trim().toLowerCase(),
          reporter_phone_e164: form.phone.trim() || undefined,
          website: form.website || undefined, // honeypot — server drops if filled
        }),
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const ticket = (await res.json().catch(() => null)) as { public_ref?: string } | null
      setDone({ ref: ticket?.public_ref ?? null })
    } catch {
      setTopError(
        `We couldn't submit your request just now. Please email ${SUPPORT_EMAIL} and we'll help you out.`,
      )
    } finally {
      setSubmitting(false)
    }
  }

  const reset = () => {
    setDone(null)
    setForm({ name: '', email: '', phone: '', topic: 'account', subject: '', message: '', website: '' })
    setErrors({})
    setTopError(null)
  }

  return (
    <div className="min-h-screen bg-[var(--color-canvas,#f7f8fa)]">
      <header className="border-b border-[var(--color-divider)] bg-white">
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-5 py-4 sm:px-6">
          <a
            href="/"
            aria-label="Back to BimaUncle home"
            className="flex items-center gap-2 text-ink-soft hover:text-ink"
          >
            <ArrowLeft size={20} />
            <img src="/logo.png" alt="BimaUncle" className="h-8 w-auto" />
          </a>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-5 py-8 sm:px-6 sm:py-10">
        <div className="flex items-center gap-2 text-brand">
          <MessageSquare size={20} />
          <span className="text-sm font-semibold uppercase tracking-wider">Support</span>
        </div>
        <h1 className="mt-3 text-2xl font-bold tracking-tight sm:text-3xl">How can we help?</h1>
        <p className="mt-3 max-w-xl text-ink-soft">
          Send us your question or issue and our team will get back to you over email.
          You can also write to{' '}
          <a href={`mailto:${SUPPORT_EMAIL}`} className="font-semibold text-ink underline">
            {SUPPORT_EMAIL}
          </a>{' '}
          directly.
        </p>

        <div className="mt-8">
          {done ? (
            <SuccessCard reference={done.ref} onReset={reset} />
          ) : (
            <form className="card" onSubmit={onSubmit} noValidate>
              {topError && (
                <div className="mb-4 rounded-input border border-red-200 bg-red-50 p-3 text-sm text-status-errorDark">
                  {topError}
                </div>
              )}

              <div className="grid gap-4 sm:grid-cols-2">
                <Field
                  id="name"
                  label="Your name"
                  error={errors.name}
                  value={form.name}
                  onChange={(v) => setForm({ ...form, name: v })}
                  autoComplete="name"
                  placeholder="Rajesh Kumar"
                />
                <Field
                  id="email"
                  label="Email"
                  type="email"
                  error={errors.email}
                  value={form.email}
                  onChange={(v) => setForm({ ...form, email: v })}
                  autoComplete="email"
                  placeholder="you@example.com"
                />
              </div>

              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <Field
                  id="phone"
                  label="Phone (optional)"
                  type="tel"
                  value={form.phone}
                  onChange={(v) => setForm({ ...form, phone: v })}
                  autoComplete="tel"
                  placeholder="+91 98765 43210"
                />
                <label htmlFor="topic" className="block">
                  <span className="text-sm font-semibold">Topic</span>
                  <select
                    id="topic"
                    value={form.topic}
                    onChange={(e) => setForm({ ...form, topic: e.target.value })}
                    className="input mt-1.5"
                  >
                    {TOPICS.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="mt-4">
                <Field
                  id="subject"
                  label="Subject"
                  error={errors.subject}
                  value={form.subject}
                  onChange={(v) => setForm({ ...form, subject: v })}
                  placeholder="Brief summary of your request"
                />
              </div>

              <label htmlFor="message" className="mt-4 block">
                <span className="text-sm font-semibold">Message</span>
                <textarea
                  id="message"
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  rows={6}
                  aria-invalid={Boolean(errors.message)}
                  aria-describedby={errors.message ? 'message-err' : undefined}
                  placeholder="Describe your question or issue in as much detail as you can."
                  className={`input mt-1.5 h-auto py-3 ${errors.message ? 'input-error' : ''}`}
                />
                {errors.message && (
                  <span id="message-err" className="mt-1.5 block text-sm text-status-errorDark">
                    {errors.message}
                  </span>
                )}
              </label>

              {/* honeypot */}
              <div className="hidden" aria-hidden="true">
                <label>
                  Website
                  <input
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={form.website}
                    onChange={(e) => setForm({ ...form, website: e.target.value })}
                  />
                </label>
              </div>

              <button type="submit" disabled={submitting} className="btn-primary mt-6 w-full">
                {submitting ? (
                  <>
                    <Loader2 size={18} className="animate-spin" /> Sending…
                  </>
                ) : (
                  'Send request'
                )}
              </button>

              <p className="mt-4 text-center text-xs text-ink-soft">
                We use your details only to respond to this request. See our{' '}
                <a href="/privacy" className="font-semibold text-ink underline">
                  Privacy Policy
                </a>
                .
              </p>
            </form>
          )}
        </div>

        <div className="mt-10 border-t border-[var(--color-divider)] pt-6">
          <a href="/" className="font-semibold text-brand underline">
            ← Back to home
          </a>
        </div>
      </main>
    </div>
  )
}

function Field({
  id,
  label,
  error,
  value,
  onChange,
  type = 'text',
  autoComplete,
  placeholder,
}: {
  id: string
  label: string
  error?: string
  value: string
  onChange: (v: string) => void
  type?: string
  autoComplete?: string
  placeholder?: string
}) {
  return (
    <label htmlFor={id} className="block">
      <span className="text-sm font-semibold">{label}</span>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        placeholder={placeholder}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `${id}-err` : undefined}
        className={`input mt-1.5 ${error ? 'input-error' : ''}`}
      />
      {error && (
        <span id={`${id}-err`} className="mt-1.5 block text-sm text-status-errorDark">
          {error}
        </span>
      )}
    </label>
  )
}

function SuccessCard({ reference, onReset }: { reference: string | null; onReset: () => void }) {
  return (
    <div className="card animate-fade-in text-center">
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50 text-status-successDark">
        <CheckCircle2 size={28} />
      </div>
      <h3 className="mt-4">Thanks — we&apos;ve got your request.</h3>
      <p className="mt-2 text-sm text-ink-soft">
        {reference ? (
          <>
            Your reference is <span className="font-semibold text-ink">{reference}</span>. We&apos;ll
            reply to your email shortly.
          </>
        ) : (
          <>We&apos;ll reply to your email shortly.</>
        )}
      </p>

      <a
        href={`mailto:${SUPPORT_EMAIL}`}
        className="mt-5 inline-flex items-center justify-center gap-2 rounded-input bg-[#0F172A] px-4 py-3 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5"
      >
        <Mail size={18} /> Email us directly
      </a>

      <div>
        <button
          type="button"
          onClick={onReset}
          className="mt-4 text-sm font-semibold text-brand hover:underline"
        >
          Send another request
        </button>
      </div>
    </div>
  )
}
